import { apiWithAuth } from "@/lib/axios";
import type { Skill } from "./types";
import { emitCopilotMutation } from "../mutations";
import { withFollow } from "./followHelpers";

export const reorderProductsSkill: Skill = {
  id: "reorderProducts",
  description:
    "Reordena los productos de una categoría (o sin categoría). Pasa orderedIds en el orden DESEADO.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      orderedIds: { type: "array", items: { type: "string" } },
      categoryId: { type: "string" },
    },
    required: ["restaurantId", "orderedIds"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const orderedIds = Array.isArray(args.orderedIds) ? args.orderedIds.map(String) : [];
    if (!restaurantId || orderedIds.length === 0) return { ok: false, error: "Faltan datos." };
    const categoryId = args.categoryId ? String(args.categoryId) : null;

    const ok = await ctx.confirmation.confirm({
      title: "Reordenar productos",
      details: [{ label: "Productos a ordenar", value: `${orderedIds.length}` }],
      confirmLabel: "Aplicar orden",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Reordenando productos…",
      successMsg: "✓ Orden aplicado",
      restaurantId,
      targetTab: "productos",
      targetTabLabel: "Productos",
      run: async () => {
        try {
          await apiWithAuth.post(
            `/restaurants/${restaurantId}/products/reorder`,
            { orderedIds, categoryId },
            ctx.accessToken,
          );
          emitCopilotMutation({ scope: "products", action: "updated", restaurantId });
          return {
            ok: true as const,
            result: { count: orderedIds.length, categoryId },
            humanSummary: "Orden de productos actualizado.",
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};

export const reorderCategoriesSkill: Skill = {
  id: "reorderCategories",
  description:
    "Reordena las categorías del menú. Pasa orderedIds en el orden deseado.",
  schema: {
    type: "object",
    properties: {
      restaurantId: { type: "string" },
      orderedIds: { type: "array", items: { type: "string" } },
    },
    required: ["restaurantId", "orderedIds"],
  },
  async run(args, ctx) {
    if (!ctx.accessToken) return { ok: false, error: "Sesión no disponible." };
    const restaurantId = String(args.restaurantId ?? "");
    const orderedIds = Array.isArray(args.orderedIds) ? args.orderedIds.map(String) : [];
    if (!restaurantId || orderedIds.length === 0) return { ok: false, error: "Faltan datos." };

    const ok = await ctx.confirmation.confirm({
      title: "Reordenar categorías",
      details: [{ label: "Categorías a ordenar", value: `${orderedIds.length}` }],
      confirmLabel: "Aplicar orden",
    });
    if (!ok) return { ok: false, error: "Acción cancelada por el usuario." };

    return withFollow(ctx, {
      intro: "Reordenando categorías…",
      successMsg: "✓ Orden aplicado",
      restaurantId,
      targetTab: "productos",
      targetTabLabel: "Productos",
      run: async () => {
        try {
          await apiWithAuth.post(
            `/restaurants/${restaurantId}/product-categories/reorder`,
            { orderedIds },
            ctx.accessToken,
          );
          emitCopilotMutation({ scope: "categories", action: "updated", restaurantId });
          return {
            ok: true as const,
            result: { count: orderedIds.length },
            humanSummary: "Orden de categorías actualizado.",
          };
        } catch (err: any) {
          return { ok: false as const, error: err?.response?.data?.message ?? "Error." };
        }
      },
    });
  },
};
